'use client'

import { ChevronDown, ChevronLeft, ChevronRight, ChevronUp } from 'lucide-react'
import * as React from 'react'
import { DayPicker } from 'react-day-picker'

import { cn } from '../cn'
import { buttonVariants } from './button'

type CalendarProps = React.ComponentProps<typeof DayPicker>

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  captionLayout = 'dropdown',
  startMonth,
  endMonth,
  ...props
}: CalendarProps) {
  // Reaching January 2030 by chevron click is 50+ clicks. Default to
  // dropdown captions (month + year as <select>s) with a sensible range
  // around today. Callers needing wider/narrower ranges pass their own
  // startMonth/endMonth; opt back to chevron-only with captionLayout="label".
  const now = new Date()
  const defaultStart = new Date(now.getFullYear() - 1, 0, 1)
  const defaultEnd = new Date(now.getFullYear() + 10, 11, 31)

  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      captionLayout={captionLayout}
      startMonth={startMonth ?? defaultStart}
      endMonth={endMonth ?? defaultEnd}
      className={cn('p-3', className)}
      classNames={{
        // `relative` is the positioning context for the absolute nav buttons.
        // rdp's own stylesheet sets this, but we don't import it — without it
        // the chevrons anchor to the popover and drift over the day grid.
        months: 'relative flex flex-col sm:flex-row gap-4',
        month: 'flex flex-col gap-4',
        month_caption: 'flex justify-center pt-1 relative items-center',
        // In dropdown mode caption_label IS the visible chip (the <select>
        // sits invisible on top to capture clicks). In label mode it's just
        // plain text.
        caption_label:
          captionLayout === 'label'
            ? 'text-sm font-medium'
            : 'inline-flex items-center gap-1 rounded-md px-2 py-1 text-sm font-medium hover:bg-accent [&>svg]:h-3.5 [&>svg]:w-3.5 [&>svg]:text-muted-foreground',
        dropdowns: 'flex items-center gap-1.5',
        dropdown_root: 'relative inline-flex items-center',
        // The select is the actual control — but visually we render the
        // caption_label. Stretch the select over the label so clicks hit
        // it; opacity-0 hides the native chrome.
        dropdown: 'absolute inset-0 z-10 cursor-pointer opacity-0 [&>option]:bg-background [&>option]:text-foreground',
        nav: 'flex items-center gap-1',
        // `top-1` pins the chevrons to the caption row (which has pt-1); without
        // an explicit top they fall to their static position mid-grid.
        button_previous: cn(
          buttonVariants({ variant: 'outline' }),
          'absolute left-1 top-1 z-10 h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100',
        ),
        button_next: cn(
          buttonVariants({ variant: 'outline' }),
          'absolute right-1 top-1 z-10 h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100',
        ),
        month_grid: 'w-full border-collapse',
        weekdays: 'flex',
        weekday: 'text-muted-foreground rounded-md w-8 font-normal text-[0.8rem]',
        week: 'flex w-full mt-2',
        // `w-8` (matching weekday) must live on the CELL, not only on
        // day_button: for days outside [startMonth, endMonth] rdp renders the
        // cell but omits the button, so a content-sized cell would collapse to
        // 0px and pack a partial first week leftwards — Tue Sep 1 landing under
        // the Su column (PROD-1181).
        day: 'relative w-8 p-0 text-center text-sm focus-within:relative focus-within:z-20',
        day_button: cn(buttonVariants({ variant: 'ghost' }), 'h-8 w-8 p-0 font-normal aria-selected:opacity-100'),
        selected:
          'bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground rounded-md',
        today: 'bg-accent text-accent-foreground rounded-md',
        outside: 'text-muted-foreground opacity-50',
        disabled: 'text-muted-foreground opacity-50',
        range_middle: 'aria-selected:bg-accent aria-selected:text-accent-foreground',
        range_end: 'rounded-md',
        hidden: 'invisible',
        ...classNames,
      }}
      components={{
        // v9 passes orientation for every chevron — including the down-arrow
        // hint inside dropdown captions. The previous fallback turned those
        // into right-arrows, producing a phantom > next to each <select>.
        Chevron: ({ orientation }) => {
          if (orientation === 'left') {
            return <ChevronLeft className="h-4 w-4" />
          }
          if (orientation === 'up') {
            return <ChevronUp className="h-4 w-4" />
          }
          if (orientation === 'down') {
            return <ChevronDown className="h-4 w-4" />
          }
          return <ChevronRight className="h-4 w-4" />
        },
      }}
      {...props}
    />
  )
}
Calendar.displayName = 'Calendar'

export { Calendar, type CalendarProps }
